import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useActiveEvent } from '@/hooks/useActiveEvent';
import { useAdminEventFilter } from '@/hooks/useAdminEventFilter';
import {
  Loader2,
  Plus,
  Edit,
  Users,
  Calendar,
  MapPin,
  Clock,
  Building2,
  Mail,
  Linkedin as LinkedinIcon,
  Filter,
  Search,
  X,
  Save,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { cn, displayName, toPacificDatetimeString, pacificDatetimeToISO, formatDatePT, formatTimePT, getDateStringPT, getEventDaysFromSessions, getEventDayStartLocal, getEventDayEndLocal } from '@/lib/utils';
import { DesktopShell } from '@/components/desktop/DesktopShell';
import { ThreePanelLayout } from '@/components/desktop/layouts/ThreePanelLayout';

interface Session {
  id: string;
  title: string;
  description?: string;
  speaker: string;
  startTime: string;
  endTime: string;
  location: string;
  sessionType?: string;
  capacity?: number;
  status: string;
  _count?: { rsvps: number };
}

interface SessionRsvp {
  id: string;
  status: string;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    organization?: string;
    department?: string;
    role?: string;
    linkedinUrl?: string;
    avatarUrl?: string;
  };
}

interface SessionRsvpsData {
  session: {
    id: string;
    title: string;
    startTime: string;
    endTime: string;
    location?: string;
    capacity: number;
  };
  rsvps: SessionRsvp[];
  confirmedCount: number;
  waitlistedCount: number;
  totalCount: number;
}

interface SessionsResponse {
  success: boolean;
  data: Session[];
}

interface SessionRsvpsResponse {
  success: boolean;
  data: SessionRsvpsData;
}

const ROLE_COLORS: Record<string, string> = {
  admin: '#3b82f6',
  faculty: '#3b82f6',
  student: '#10b981',
  industry: '#3b82f6',
  staff: '#8b5cf6',
};

const ALL_STATUSES = ['pending', 'scheduled', 'cancelled'];

const SESSION_TYPES = [
  'Featured',
  'Networking',
  'Panel',
  'Breakout',
  'Workshop',
];

// Status is now managed via column 3 header buttons, not the modal form

interface SessionFormData {
  title: string;
  description: string;
  speaker: string;
  startTime: string;
  endTime: string;
  location: string;
  sessionType: string;
  capacity?: number;
  status: string;
}

const DEFAULT_FORM: SessionFormData = {
  title: '',
  description: '',
  speaker: '',
  startTime: '',
  endTime: '',
  location: '',
  sessionType: '',
  capacity: undefined,
  status: 'scheduled',
};

export default function SessionManagement() {
  const [searchParams] = useSearchParams();
  const { event: activeEvent } = useActiveEvent();
  const { eventFilter, setEventFilter, events, fetchEvents, getResolvedEventId } = useAdminEventFilter();
  const eventMinDate = activeEvent ? getEventDayStartLocal(activeEvent.dateStart) : '';
  const eventMaxDate = activeEvent ? getEventDayEndLocal(activeEvent.dateEnd) : '';
  const [sessions, setSessions] = useState<Session[]>([]);
  const eventDays = useMemo(
    () => getEventDaysFromSessions(sessions.map((s) => s.startTime)),
    [sessions]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    searchParams.get('sessionId')
  );
  const [rsvpData, setRsvpData] = useState<SessionRsvpsData | null>(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);

  // Filter state
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterSessionType, setFilterSessionType] = useState('');
  const [filterDate, setFilterDate] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSessionId, setModalSessionId] = useState<string | null>(null);
  const [formData, setFormData] = useState<SessionFormData>(DEFAULT_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    fetchEvents().then(() => fetchSessions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when event filter changes (skip initial mount — handled above)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventFilter]);

  useEffect(() => {
    if (selectedSessionId) {
      fetchRsvps(selectedSessionId);
    } else {
      setRsvpData(null);
    }
  }, [selectedSessionId]);

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      const eventId = getResolvedEventId();
      if (eventId) params.set('eventId', eventId);

      const response = await api.get<SessionsResponse>(`/admin/sessions?${params}`);
      const sessionsData = Array.isArray(response) ? response : (response.data || []);
      setSessions(sessionsData);
    } catch (error) {
      console.error('Failed to fetch sessions', error);
      toast.error('Failed to load sessions');
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchRsvps = async (sessionId: string) => {
    setRsvpLoading(true);
    try {
      const response = await api.get<SessionRsvpsResponse>(`/admin/sessions/${sessionId}/rsvps`);
      if (response.success && response.data) {
        setRsvpData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch RSVPs:', error);
      toast.error('Failed to load session participants');
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelConfirmId) return;
    setCancelLoading(true);
    try {
      await api.patch(`/admin/sessions/${cancelConfirmId}`, { status: 'cancelled' });
      setCancelConfirmId(null);
      toast.success('Session cancelled and attendees notified');
      fetchSessions();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to cancel session');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSchedule = async (sessionId: string) => {
    try {
      await api.patch(`/admin/sessions/${sessionId}`, { status: 'scheduled' });
      toast.success('Session scheduled');
      fetchSessions();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to schedule session');
    }
  };

  const openCreateModal = () => {
    setModalSessionId(null);
    setFormData(DEFAULT_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEditModal = async (sessionId: string) => {
    setModalSessionId(sessionId);
    setFormData(DEFAULT_FORM);
    setFormError(null);
    setFormLoading(true);
    setModalOpen(true);
    try {
      const response = await api.get<{ success: boolean; data: any }>(`/sessions/${sessionId}`);
      const s = response.data;
      setFormData({
        title: s.title,
        description: s.description || '',
        speaker: s.speaker || '',
        startTime: toPacificDatetimeString(s.startTime),
        endTime: toPacificDatetimeString(s.endTime),
        location: s.location || '',
        sessionType: s.sessionType || '',
        capacity: s.capacity || undefined,
        status: s.status,
      });
    } catch (error: any) {
      setFormError(error.response?.data?.error || 'Failed to load session');
      toast.error('Failed to load session');
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'capacity' ? (value ? parseInt(value) : undefined) : value,
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSaving(true);
    try {
      const submitData: Record<string, any> = {
        ...formData,
        startTime: pacificDatetimeToISO(formData.startTime),
        endTime: pacificDatetimeToISO(formData.endTime),
      };
      if (!submitData.speaker) delete submitData.speaker;
      if (!submitData.sessionType) delete submitData.sessionType;
      // Status is managed via column 3 header buttons, not the modal
      delete submitData.status;

      if (modalSessionId) {
        await api.patch(`/admin/sessions/${modalSessionId}`, submitData);
        toast.success('Session updated successfully');
      } else {
        await api.post('/admin/sessions', submitData);
        toast.success('Session created successfully');
      }
      setModalOpen(false);
      fetchSessions();
    } catch (error: any) {
      const action = modalSessionId ? 'update' : 'create';
      setFormError(error.response?.data?.error || `Failed to ${action} session`);
      toast.error(`Failed to ${action} session`);
    } finally {
      setFormSaving(false);
    }
  };

  const formatSessionDateTime = (dateString: string | undefined | null) => {
    if (!dateString) return 'TBD';
    try {
      return `${formatDatePT(dateString)}, ${formatTimePT(dateString)}`;
    } catch {
      return 'TBD';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-muted text-gray-800';
    }
  };

  const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'pending': return 'secondary';
      case 'scheduled': return 'default';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const sessionTypes = SESSION_TYPES;

  const filteredSessions = useMemo(() => {
    return sessions.filter((s) => {
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (
          !s.title.toLowerCase().includes(q) &&
          !(s.speaker || '').toLowerCase().includes(q) &&
          !(s.location || '').toLowerCase().includes(q)
        ) return false;
      }
      if (filterStatuses.length > 0 && !filterStatuses.includes(s.status || 'scheduled')) return false;
      if (filterSessionType && s.sessionType !== filterSessionType) return false;
      if (filterDate) {
        const sessionDate = getDateStringPT(s.startTime);
        if (sessionDate !== filterDate) return false;
      }
      return true;
    });
  }, [sessions, filterSearch, filterStatuses, filterSessionType, filterDate]);

  // Clear selection when it's no longer in the visible list
  useEffect(() => {
    if (selectedSessionId && !filteredSessions.some((s) => s.id === selectedSessionId)) {
      setSelectedSessionId(null);
    }
  }, [filteredSessions, selectedSessionId]);

  const activeFilterCount =
    (filterSearch ? 1 : 0) + filterStatuses.length + (filterSessionType ? 1 : 0) + (filterDate ? 1 : 0);

  const clearFilters = () => {
    setFilterSearch('');
    setFilterStatuses([]);
    setFilterSessionType('');
    setFilterDate('');
  };

  const toggleStatus = (status: string) =>
    setFilterStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );

  const selectedSession = filteredSessions.find((s) => s.id === selectedSessionId) ?? null;
  const confirmedRsvps = rsvpData?.rsvps.filter((r) => r.status === 'confirmed') ?? [];
  const waitlistedRsvps = rsvpData?.rsvps.filter((r) => r.status === 'waitlisted') ?? [];

  const renderParticipantList = (participants: SessionRsvp[]) => (
    <div className="space-y-3 p-4">
      {participants.map((rsvp) => (
        <div key={rsvp.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
          <Avatar className="h-9 w-9">
            <AvatarImage src={rsvp.user.avatarUrl || undefined} />
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {getInitials(displayName(rsvp.user))}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm">{displayName(rsvp.user)}</span>
              {rsvp.user.role && (
                <Badge
                  variant="outline"
                  className="text-xs"
                  style={{
                    borderColor: ROLE_COLORS[rsvp.user.role] || '#6b7280',
                    color: ROLE_COLORS[rsvp.user.role] || '#6b7280',
                  }}
                >
                  {rsvp.user.role}
                </Badge>
              )}
            </div>
            {rsvp.user.organization && (
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {rsvp.user.organization}
                {rsvp.user.department && ` · ${rsvp.user.department}`}
              </p>
            )}
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <a href={`mailto:${rsvp.user.email}`} className="flex items-center gap-1 text-xs text-primary hover:underline">
                <Mail className="h-3 w-3" />
                {rsvp.user.email}
              </a>
              {rsvp.user.linkedinUrl && (
                <a href={rsvp.user.linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                  <LinkedinIcon className="h-3 w-3" />
                  LinkedIn
                </a>
              )}
            </div>
            <p className="text-xs text-muted-foreground/70 mt-1">RSVP'd: {getTimeAgo(rsvp.createdAt)}</p>
          </div>
        </div>
      ))}
    </div>
  );

  // Left Panel — Filters
  const FiltersPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Filter className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Filters</h2>
            <p className="text-sm text-muted-foreground">Refine your search</p>
          </div>
        </div>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-[11px] px-2">
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="px-3 py-3 space-y-3">
          {/* Event Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Event</Label>
            <RadioGroup value={eventFilter} onValueChange={(v) => { setEventFilter(v); setSelectedSessionId(null); }} className="gap-0.5">
              <div className="flex items-center gap-1.5 py-0.5">
                <RadioGroupItem value="all" id="event-all" className="h-3.5 w-3.5" />
                <label htmlFor="event-all" className="text-xs cursor-pointer">All Events</label>
              </div>
              <div className="flex items-center gap-1.5 py-0.5">
                <RadioGroupItem value="active" id="event-active" className="h-3.5 w-3.5" />
                <label htmlFor="event-active" className="text-xs cursor-pointer">Active Event</label>
              </div>
              {events
                .filter((ev) => ev.status === 'archived')
                .sort((a, b) => new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime())
                .map((ev) => (
                  <div key={ev.id} className="flex items-center gap-1.5 py-0.5">
                    <RadioGroupItem value={ev.id} id={`event-${ev.id}`} className="h-3.5 w-3.5" />
                    <label htmlFor={`event-${ev.id}`} className="text-xs cursor-pointer flex items-center justify-between w-full">
                      <span className="truncate">{ev.name}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0 ml-1">
                        {new Date(ev.dateStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </span>
                    </label>
                  </div>
                ))}
            </RadioGroup>
          </div>

          <Separator />

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Title, speaker, location..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              className="pl-8 h-8 text-xs"
            />
            {filterSearch && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0.5 top-1/2 -translate-y-1/2 h-6 w-6"
                onClick={() => setFilterSearch('')}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>

          <Separator />

          {/* Date Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Event Day</Label>
            <div className="space-y-0.5">
              {eventDays.map((day) => (
                <div key={day.date} className="flex items-center gap-2">
                  <Checkbox
                    id={`date-${day.date}`}
                    checked={filterDate === day.date}
                    onCheckedChange={() => setFilterDate(filterDate === day.date ? '' : day.date)}
                  />
                  <label htmlFor={`date-${day.date}`} className="text-xs cursor-pointer">
                    {day.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Status Filter */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Status</Label>
            <div className="space-y-0.5">
              {ALL_STATUSES.map((status) => (
                <div key={status} className="flex items-center gap-2">
                  <Checkbox
                    id={`status-${status}`}
                    checked={filterStatuses.includes(status)}
                    onCheckedChange={() => toggleStatus(status)}
                  />
                  <label
                    htmlFor={`status-${status}`}
                    className="text-xs cursor-pointer capitalize flex items-center gap-2"
                  >
                    <span
                      className={cn(
                        'px-1.5 py-0.5 text-[10px] font-medium rounded-full capitalize',
                        getStatusColor(status)
                      )}
                    >
                      {status.replace('_', ' ')}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Session Type Filter */}
          <Separator />
          <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Session Type</Label>
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="sessionType-all"
                      checked={filterSessionType === ''}
                      onCheckedChange={() => setFilterSessionType('')}
                    />
                    <label htmlFor="sessionType-all" className="text-xs cursor-pointer text-muted-foreground">
                      All types
                    </label>
                  </div>
                  {sessionTypes.map((type) => (
                    <div key={type} className="flex items-center gap-2">
                      <Checkbox
                        id={`sessionType-${type}`}
                        checked={filterSessionType === type}
                        onCheckedChange={() => setFilterSessionType(filterSessionType === type ? '' : type)}
                      />
                      <label htmlFor={`sessionType-${type}`} className="text-xs cursor-pointer">
                        {type}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
        </div>
      </ScrollArea>
    </div>
  );

  const CardSkeleton = () => (
    <div className="flex items-center gap-3 px-6 py-3 border-b border-border">
      <Skeleton className="w-9 h-9 rounded-lg" />
      <div className="flex-1">
        <Skeleton className="h-4 w-40 mb-1.5" />
        <Skeleton className="h-3 w-28" />
      </div>
      <Skeleton className="h-5 w-16 rounded-full" />
    </div>
  );

  // Center Panel — Session List
  const CenterPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Calendar className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Sessions</h2>
            <p className="text-sm text-muted-foreground">
              {isLoading ? 'Loading...' : `${filteredSessions.length} of ${sessions.length} sessions`}
            </p>
          </div>
        </div>
        <Button size="sm" className="gap-1.5" onClick={openCreateModal}>
          <Plus className="h-4 w-4" />
          Create Session
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)
        ) : filteredSessions.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">No sessions found</p>
            {sessions.length > 0 && (
              <p className="text-sm mt-1">Try adjusting your filters</p>
            )}
          </div>
        ) : (
          filteredSessions.map((session) => {
            const isCancelled = session.status === 'cancelled';
            return (
              <button
                key={session.id}
                className={`w-full flex items-center gap-3 px-6 py-3 border-b border-border text-left transition-colors hover:bg-muted/50 ${
                  selectedSessionId === session.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                }`}
                onClick={() => setSelectedSessionId(session.id === selectedSessionId ? null : session.id)}
              >
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Calendar className="h-4 w-4 text-primary" />
                </div>
                <div className={cn('flex-1 min-w-0', isCancelled && 'opacity-60')}>
                  <p className={cn('font-medium text-sm truncate', isCancelled && 'line-through')}>{session.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {session.speaker || 'No speaker'} &middot; {session._count?.rsvps || 0}{session.capacity ? ` / ${session.capacity}` : ''} RSVPs
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className={cn('text-[10px] flex-shrink-0 capitalize', isCancelled && 'opacity-60')}
                  style={{
                    borderColor: session.status === 'cancelled' ? '#ef4444' : session.status === 'pending' ? '#eab308' : '#22c55e',
                    color: session.status === 'cancelled' ? '#ef4444' : session.status === 'pending' ? '#eab308' : '#22c55e',
                  }}
                >
                  {(session.status || 'scheduled').replace('_', ' ')}
                </Badge>
                <button
                  type="button"
                  className="p-1.5 rounded-md transition-colors flex-shrink-0 text-blue-600 hover:bg-blue-50"
                  title={isCancelled ? 'Edit to reschedule' : 'Edit session'}
                  onClick={(e) => { e.stopPropagation(); openEditModal(session.id); }}
                >
                  <Edit className="h-3.5 w-3.5" />
                </button>
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  // Right Panel — Session Detail & RSVPs
  const RightPanel = (
    <div className="h-full flex flex-col">
      {/* Right panel header */}
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Clock className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-lg">Session Details</h2>
            <p className="text-sm text-muted-foreground truncate">
              {selectedSession ? selectedSession.title : 'Select a session'}
            </p>
          </div>
        </div>
        {selectedSession && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {(selectedSession.status === 'pending' || selectedSession.status === 'cancelled') && (
              <Button
                size="sm"
                variant="default"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => handleSchedule(selectedSession.id)}
              >
                Schedule
              </Button>
            )}
            {(selectedSession.status === 'pending' || selectedSession.status === 'scheduled') && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setCancelConfirmId(selectedSession.id)}
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>

      {selectedSession ? (
        <>
          {/* Session Details */}
          <div className="flex-shrink-0 p-5 border-b border-border space-y-3">
            <div>
              <h3 className="text-base font-bold leading-tight">{selectedSession.title}</h3>
              {selectedSession.speaker && (
                <p className="text-sm text-muted-foreground mt-0.5">{selectedSession.speaker}</p>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={getStatusBadgeVariant(selectedSession.status || 'scheduled')} className="capitalize text-xs">
                {(selectedSession.status || 'scheduled').replace('_', ' ')}
              </Badge>
              {selectedSession.sessionType && (
                <Badge variant="outline" className="text-xs">{selectedSession.sessionType}</Badge>
              )}
            </div>

            <div className="space-y-1.5 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {formatSessionDateTime(selectedSession.startTime)}
                  {selectedSession.endTime && ` – ${formatTimePT(selectedSession.endTime)}`}
                </span>
              </div>
              {selectedSession.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span>{selectedSession.location}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 shrink-0" />
                <span>
                  {selectedSession._count?.rsvps || 0}
                  {selectedSession.capacity ? ` / ${selectedSession.capacity} capacity` : ' attendees'}
                </span>
              </div>
            </div>

            {selectedSession.description && (
              <p className="text-sm text-foreground/80 leading-relaxed">
                {selectedSession.description}
              </p>
            )}
          </div>

          {/* RSVP Tabs */}
          <div className="flex-1 overflow-hidden">
            {rsvpLoading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <Skeleton className="h-9 w-9 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-32 mb-2" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Tabs defaultValue="confirmed" className="flex flex-col h-full">
                <TabsList className="grid grid-cols-2 mx-4 mt-4 flex-shrink-0">
                  <TabsTrigger value="confirmed" className="gap-1.5 text-xs">
                    Confirmed
                    <Badge variant="secondary" className="text-xs px-1.5">{rsvpData?.confirmedCount ?? 0}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="waitlisted" className="gap-1.5 text-xs">
                    Waitlisted
                    <Badge variant="secondary" className="text-xs px-1.5">{rsvpData?.waitlistedCount ?? 0}</Badge>
                  </TabsTrigger>
                </TabsList>
                <ScrollArea className="flex-1 mt-2">
                  <TabsContent value="confirmed" className="mt-0">
                    {confirmedRsvps.length > 0 ? renderParticipantList(confirmedRsvps) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No confirmed participants yet</p>
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value="waitlisted" className="mt-0">
                    {waitlistedRsvps.length > 0 ? renderParticipantList(waitlistedRsvps) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No participants on waitlist</p>
                      </div>
                    )}
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center text-muted-foreground p-8">
          <div className="text-center">
            <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">Select a session</p>
            <p className="text-sm mt-1">Click any session row to view details and participant RSVPs</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <DesktopShell>
        <div className="h-full flex flex-col">
          {/* Page Title Bar */}
          <div className="px-6 py-4 border-b border-border bg-background flex-shrink-0">
            <h1 className="text-2xl font-bold">Sessions</h1>
          </div>

          <div className="flex-1 overflow-hidden">
            <ThreePanelLayout
              left={FiltersPanel}
              center={CenterPanel}
              right={RightPanel}
              leftWidth="260px"
              equalCenterRight
              connectedPanels={!!selectedSession}
            />
          </div>
        </div>
      </DesktopShell>

      {/* Cancel Session Confirmation */}
      <AlertDialog open={!!cancelConfirmId} onOpenChange={(open) => !cancelLoading && !open && setCancelConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Session?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the session and send an in-app notification to all RSVP'd attendees.
              Cancelled sessions remain visible in the admin view but are hidden from attendees.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelLoading}>Go Back</AlertDialogCancel>
            <Button
              onClick={handleCancel}
              disabled={cancelLoading}
            >
              {cancelLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Cancel Session'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create / Edit Session Modal */}
      <Dialog open={modalOpen} onOpenChange={(open) => !formSaving && setModalOpen(open)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{modalSessionId ? 'Edit Session' : 'Create Session'}</DialogTitle>
          </DialogHeader>

          {formLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <DialogClose asChild>
                <Button type="button" variant="outline" size="sm">
                  Cancel
                </Button>
              </DialogClose>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="space-y-4 pt-2">
              {formError && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}

              {/* Title */}
              <div>
                <Label htmlFor="modal-title" className="text-sm font-medium mb-1.5 block">
                  Session Title *
                </Label>
                <Input
                  id="modal-title"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  minLength={5}
                  maxLength={200}
                  placeholder="e.g., AI/ML in Autonomous Systems"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="modal-description" className="text-sm font-medium mb-1.5 block">
                  Description *
                </Label>
                <Textarea
                  id="modal-description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={4}
                  placeholder="Provide a detailed description of the session content..."
                />
                <p className="text-xs text-muted-foreground mt-1">{formData.description.length} / 2000</p>
              </div>

              {/* Speaker */}
              <div>
                <Label htmlFor="modal-speaker" className="text-sm font-medium mb-1.5 block">
                  Speaker
                </Label>
                <Input
                  id="modal-speaker"
                  name="speaker"
                  value={formData.speaker}
                  onChange={handleFormChange}
                  maxLength={100}
                  placeholder="e.g., Dr. Jane Smith"
                />
              </div>

              {/* Start & End Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="modal-startTime" className="text-sm font-medium mb-1.5 block">
                    Start Time (PT) *
                  </Label>
                  <Input
                    id="modal-startTime"
                    name="startTime"
                    type="datetime-local"
                    value={formData.startTime}
                    onChange={handleFormChange}
                    required
                    min={eventMinDate}
                    max={eventMaxDate}
                  />
                </div>
                <div>
                  <Label htmlFor="modal-endTime" className="text-sm font-medium mb-1.5 block">
                    End Time (PT) *
                  </Label>
                  <Input
                    id="modal-endTime"
                    name="endTime"
                    type="datetime-local"
                    value={formData.endTime}
                    onChange={handleFormChange}
                    required
                    min={eventMinDate}
                    max={eventMaxDate}
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <Label htmlFor="modal-location" className="text-sm font-medium mb-1.5 block">
                  Location *
                </Label>
                <Input
                  id="modal-location"
                  name="location"
                  value={formData.location}
                  onChange={handleFormChange}
                  required
                  minLength={2}
                  maxLength={100}
                  placeholder="e.g., Main Auditorium"
                />
              </div>

              {/* Session Type & Capacity */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Session Type</Label>
                  <Select
                    value={formData.sessionType || '__none__'}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, sessionType: value === '__none__' ? '' : value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {SESSION_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="modal-capacity" className="text-sm font-medium mb-1.5 block">
                    Capacity (Optional)
                  </Label>
                  <Input
                    id="modal-capacity"
                    name="capacity"
                    type="number"
                    value={formData.capacity || ''}
                    onChange={handleFormChange}
                    min={1}
                    placeholder="e.g., 50"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={formSaving}
                  >
                    Cancel
                  </Button>
                </DialogClose>
                <Button type="submit" disabled={formSaving} className="gap-2">
                  {formSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {modalSessionId ? 'Update' : 'Create'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
